import { useEffect, useState } from 'react';
import { regenApi } from '../lib/api';
import { Loader2, RefreshCw, Image as ImageIcon, X } from 'lucide-react';

// ── Storyboard Images — read-only gallery of the stills the storyboard flow generated, grouped by
// session (newest first), each row ordered by scene. Fetches GET /regen/storyboard/images on mount;
// beat text is the caption/tooltip. Click a still → lightbox (mirrors OrbitGallery). ─────────────────
export type StoryboardStill = { scene_index: number; image_url: string; beat_text?: string };
export type StoryboardSession = { session_id: string; created_at?: string; images: StoryboardStill[] };

export function StoryboardImages() {
  const [sessions, setSessions] = useState<StoryboardSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewer, setViewer] = useState<StoryboardStill | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await regenApi.storyboardImages();
      setSessions(r.data?.sessions || []);
    } catch { /* leave empty */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading && !sessions.length) return <div className="flex justify-center py-16 text-gray-400"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-card p-4 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white"><ImageIcon className="inline w-3.5 h-3.5 mr-1 align-[-2px]" /> Storyboard Images</h2>
          <p className="text-sm text-gray-500 mt-1">Every <b>still</b> the storyboard flow has generated, grouped by session in scene order. Read-only — click any image to enlarge.</p>
        </div>
        <button onClick={load} className="shrink-0 text-xs text-gray-400 hover:text-gray-200 inline-flex items-center gap-1"><RefreshCw className="w-3.5 h-3.5" /> refresh</button>
      </div>

      {!sessions.length && <div className="text-sm text-gray-500 py-12 text-center">No storyboard stills yet — generate some in the <b>Storyboard</b> tab and they land here.</div>}

      {sessions.map((s) => (
        <div key={s.session_id} className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-sm text-gray-900 dark:text-white">{s.images.length} scene{s.images.length === 1 ? '' : 's'}</h3>
            {s.created_at && <span className="text-[11px] text-gray-400">{new Date(s.created_at).toLocaleString()}</span>}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {[...s.images].sort((a, b) => a.scene_index - b.scene_index).map((img) => (
              <button key={img.scene_index} type="button" onClick={() => setViewer(img)} title={img.beat_text || ''}
                className="relative rounded-lg overflow-hidden border border-border hover:border-primary/50 text-left">
                <div className="aspect-[9/16] bg-black flex items-center justify-center">
                  <img src={img.image_url} alt={img.beat_text || ''} className="w-full h-full object-contain" loading="lazy" />
                </div>
                <span className="absolute bottom-0 inset-x-0 px-1 py-0.5 bg-black/70 text-white text-[9px] truncate">Scene {img.scene_index + 1}{img.beat_text ? ` · ${img.beat_text}` : ''}</span>
              </button>
            ))}
          </div>
        </div>
      ))}

      {/* Lightbox — mirrors OrbitGallery's still viewer. */}
      {viewer && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={() => setViewer(null)}>
          <div className="relative max-h-[90vh] max-w-[92vw]" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setViewer(null)} className="absolute -top-3 -right-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-black/70 text-white ring-1 ring-white/20 hover:bg-black"><X className="w-5 h-5" /></button>
            <img src={viewer.image_url} alt="" className="max-h-[82vh] max-w-full rounded-2xl object-contain" />
            {viewer.beat_text && <div className="mt-3 text-xs text-gray-400 line-clamp-2 max-w-md">{viewer.beat_text}</div>}
          </div>
        </div>
      )}
    </div>
  );
}

export default StoryboardImages;
